import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import { Search, Download, Users as UsersIcon, RefreshCw, Trash2, CheckCircle2, XCircle, Clock, UserPlus, FolderOpen, Eye, ShieldPlus, Footprints } from "lucide-react";
import InviteAdminModal from "../components/admin/InviteAdminModal";
import UserJourneyModal from "../components/admin/UserJourneyModal";

const QUIZ_BAND_LABELS = {
  lt_100k: "< KES 100K/mo",
  "100k_1m": "KES 100K–1M/mo",
  "1m_5m": "KES 1M–5M/mo",
  "5m_15m": "KES 5M–15M/mo",
  gt_15m: "KES 15M+/mo",
};

const EMAIL_STATUS_META = {
  active:       { label: "Active",          cls: "bg-emerald-500/10 text-emerald-300" },
  unsubscribed: { label: "Unsubscribed · funding OK", cls: "bg-amber-500/10 text-amber-300" },
  dormant:      { label: "Dormant",         cls: "bg-red-500/10 text-red-300" },
};

function EmailStatusCell({ u, onChange }) {
  const st = EMAIL_STATUS_META[u.email_status] ? u.email_status : "active";
  const meta = EMAIL_STATUS_META[st];
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-block w-fit whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}
            data-testid={`admin-users-email-badge-${u.user_id}`}>{meta.label}</span>
      <select value={st} onChange={(e) => onChange(e.target.value)}
              data-testid={`admin-users-email-select-${u.user_id}`}
              className="w-fit rounded border border-neutral-800 bg-neutral-950 px-1.5 py-0.5 text-[11px] text-neutral-300 focus:border-[#F59E0B] focus:outline-none">
        <option value="active">Active</option>
        <option value="unsubscribed">Unsubscribed · funding OK</option>
        <option value="dormant">Dormant</option>
      </select>
    </div>
  );
}

// First three columns stay pinned while the table scrolls horizontally so
// the admin always sees who each row belongs to.
const STICKY_TH = "sticky z-20 bg-neutral-900 px-4 py-3 whitespace-nowrap";
const STICKY_TD = "sticky z-10 bg-neutral-900 group-hover:bg-[#1c1c1c] px-4 py-3 whitespace-nowrap";
const STICKY_EDGE = "shadow-[6px_0_10px_-6px_rgba(0,0,0,0.85)]";

const ROLE_COLOR = {
  founder: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  funder: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  admin: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",
};

const BILLING_COLOR = {
  trialing: "text-sky-300",
  subscribed: "text-emerald-300",
  expired: "text-red-400",
};

function renderUserBusinessOrMandate(u) {
  if (u.role === "founder") {
    return u.company
      ? <span className="font-semibold text-neutral-100">{u.company}</span>
      : <span className="text-neutral-500">No business yet</span>;
  }
  if (u.role === "funder") {
    return u.mandate_confirmed_at
      ? <span className="text-emerald-300">Mandate set</span>
      : <span className="text-neutral-500">No mandate</span>;
  }
  return <span className="text-neutral-500">—</span>;
}

function renderUserStatus(u) {
  if (u.journey_stage) {
    const stage = u.journey_stage;
    const tone = /unlocked|Subscribed|Verified/.test(stage) ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : /Awaiting approval|pending|Pending|Snapshot|Signed up|Intake|Onboarding|Awaiting analysis/.test(stage) ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
      : /expired|Rejected|locked/.test(stage) ? "border-neutral-700 bg-neutral-800/60 text-neutral-300"
      : "border-sky-500/30 bg-sky-500/10 text-sky-300";
    return (
      <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold ${tone}`}
        data-testid={`admin-users-stage-${u.user_id}`}>
        {stage}
      </span>
    );
  }
  if (u.role === "founder") {
    return (
      <span className="text-neutral-300">
        {u.status || "—"} {u.readiness_score ? <span className="text-neutral-500">· score {u.readiness_score}</span> : null}
      </span>
    );
  }
  if (u.role === "funder") {
    return (
      <span className={BILLING_COLOR[u.billing_state] || "text-neutral-300"}>
        {u.billing_state || "—"}
      </span>
    );
  }
  return <span className="text-neutral-500">—</span>;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [journeyUser, setJourneyUser] = useState(null);
  const [role, setRole] = useState("");
  const [days, setDays] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (days) params.set("days", days);
    if (emailStatus) params.set("email_status", emailStatus);
    if (q.trim()) params.set("q", q.trim());
    try {
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("[AdminUsers] load failed", e);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [role, days, emailStatus]);

  const setUserEmailStatus = async (u, status) => {
    try {
      const { data } = await api.put(`/admin/users/${u.user_id}/email-status`, { status });
      setUsers((curr) => curr.map((x) => x.user_id === u.user_id
        ? { ...x, email_status: data.status, funding_outreach_ok: data.funding_outreach_ok } : x));
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("[AdminUsers] email-status failed", e);
    }
  };

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const downloadCsv = async () => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (days) params.set("days", days);
    const url = `/admin/users.csv?${params.toString()}`;
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clinq-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const deleteUser = async (u) => {
    const ok = window.prompt(`Permanently delete ${u.email}?\n\nType DELETE to confirm.`);
    if (ok !== "DELETE") return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDelete = async (mode, label) => {
    // First do a dry-run so we can show the admin exactly how many users +
    // marketing leads will be removed before they confirm.
    let preview;
    try {
      const { data } = await api.post(`/admin/users/bulk-delete`,
        { mode, dry_run: true });
      preview = data;
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't preview bulk delete");
      return;
    }
    if (!preview.users_would_delete && !preview.marketing_leads_would_delete) {
      window.alert(`No matching records for "${label}". Nothing to delete.`);
      return;
    }
    const sample = (preview.sample_emails || []).slice(0, 8).join("\n  ");
    const ok = window.prompt(
      `BULK DELETE: ${label}\n\n` +
      `Users to delete: ${preview.users_would_delete}\n` +
      `Marketing leads to delete: ${preview.marketing_leads_would_delete}\n\n` +
      (sample ? `Sample emails:\n  ${sample}\n\n` : "") +
      `This cannot be undone. Your own admin account is always preserved.\n\n` +
      `Type DELETE to confirm.`
    );
    if (ok !== "DELETE") return;
    try {
      const { data } = await api.post(`/admin/users/bulk-delete`, { mode });
      window.alert(
        `Deleted ${data.users_deleted} user(s)` +
        (data.marketing_leads_deleted
          ? ` + ${data.marketing_leads_deleted} marketing lead(s)` : "") +
        ".");
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const approveFunder = async (u) => {
    if (!window.confirm(`Approve ${u.email} as a funder? They'll get full platform access.`)) return;
    try {
      await api.post(`/admin/funders/${u.user_id}/approve`);
      setUsers((prev) => prev.map((x) =>
        x.user_id === u.user_id ? { ...x, funder_approval_status: "approved" } : x));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Approve failed");
    }
  };

  const rejectFunder = async (u) => {
    const reason = window.prompt(`Reject ${u.email}?\n\nOptional reason (shown to the funder so they can re-apply):`);
    if (reason === null) return;
    try {
      await api.post(`/admin/funders/${u.user_id}/reject`, { reason });
      setUsers((prev) => prev.map((x) =>
        x.user_id === u.user_id
          ? { ...x, funder_approval_status: "rejected", rejection_reason: reason || "" }
          : x));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Reject failed");
    }
  };

  const generateTestAccounts = async () => {
    try {
      const { data } = await api.post("/admin/test-accounts/create", {});
      const lines = data.accounts.map(a =>
        `${a.role.toUpperCase()}\n  email: ${a.email}\n  password: ${a.password}`
      ).join("\n\n");
      try { await navigator.clipboard.writeText(lines); } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AdminUsers] clipboard failed", err);
      }
      window.alert(
        `Test accounts created (copied to clipboard):\n\n${lines}\n\n` +
        `Save the passwords now — they cannot be recovered. ` +
        `These accounts will be cleaned up by the next "Delete test accounts" purge.`
      );
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't create test accounts");
    }
  };

  const seedDemoPipeline = async () => {
    const raw = window.prompt(
      "How many demo founders to spawn? (1–10, default 8)\n\n" +
      "Each gets a different sector/country/stage/readiness profile, plus 1 " +
      "broad-mandate funder so matches show up immediately on every dashboard."
    );
    if (raw === null) return;
    const count = parseInt(raw, 10) || 8;
    try {
      const { data } = await api.post("/admin/test-accounts/seed-pipeline", { count });
      const summary =
        `Created ${data.founders_created} demo founders + 1 funder.\n\n` +
        `Shared password (works for all): ${data.shared_password}\n\n` +
        `Primary founder login: ${data.primary_founder_email}\n` +
        `Funder login: ${data.funder_email}\n\n` +
        `Founders by readiness:\n` +
        data.founders.map(f =>
          `  ${f.is_primary ? "★" : "·"} ${f.company_name} (CCR ${f.readiness_score}) — ${f.email}`
        ).join("\n");
      try { await navigator.clipboard.writeText(summary); } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AdminUsers] clipboard failed", err);
      }
      window.alert(summary + "\n\n(Summary copied to clipboard.)");
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't seed pipeline");
    }
  };

  const counts = useMemo(() => {
    const c = { founder: 0, funder: 0, admin: 0 };
    for (const u of users) c[u.role] = (c[u.role] || 0) + 1;
    return c;
  }, [users]);

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold" data-testid="admin-users-heading">Users & signups</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Everyone who has signed up to Clinq — founders, funders, and admins.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-users-invite-admin"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/20"
            title="Email a one-time admin invite link — no shared signup code."
          >
            <ShieldPlus className="h-3.5 w-3.5" /> Invite admin
          </button>
          <button
            data-testid="admin-users-generate-test"
            onClick={generateTestAccounts}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20"
            title="Spin up a fresh founder + funder pair for QA. Passwords shown once."
          >
            <UserPlus className="h-3.5 w-3.5" /> Generate test accounts
          </button>
          <button
            data-testid="admin-users-seed-pipeline"
            onClick={seedDemoPipeline}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs font-semibold text-sky-300 hover:bg-sky-500/20"
            title="Spawn 5–10 varied founders + 1 broad-mandate funder for demo screenshots / investor pitches."
          >
            <UserPlus className="h-3.5 w-3.5" /> Seed demo pipeline
          </button>
          <button
            data-testid="admin-users-bulk-test"
            onClick={() => bulkDelete("test_accounts", "All seeded test accounts (test_*, @clinqtest.io)")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/20"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete test accounts
          </button>
          <button
            data-testid="admin-users-bulk-non-admin"
            onClick={() => bulkDelete("all_non_admin", "All founders + funders (everyone except admins)")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/20"
          >
            <Trash2 className="h-3.5 w-3.5" /> Wipe all non-admins
          </button>
          <button
            data-testid="admin-users-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-xs font-semibold text-neutral-300 hover:bg-neutral-800"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-testid="admin-users-export-csv"
            onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
        <form onSubmit={onSearchSubmit} className="md:col-span-2">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="admin-users-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-lg border border-neutral-800 bg-neutral-900 py-2 pl-9 pr-3 text-sm text-neutral-100 placeholder-neutral-500 focus:border-[#F59E0B] focus:outline-none"
            />
          </label>
        </form>
        <select
          data-testid="admin-users-filter-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none"
        >
          <option value="">All roles</option>
          <option value="founder">Founders</option>
          <option value="funder">Funders</option>
          <option value="admin">Admins</option>
        </select>
        <select
          data-testid="admin-users-filter-days"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none"
        >
          <option value="">All time</option>
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <select
          data-testid="admin-users-filter-email-status"
          value={emailStatus}
          onChange={(e) => setEmailStatus(e.target.value)}
          className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none"
        >
          <option value="">All email statuses</option>
          <option value="active">Active (receiving emails)</option>
          <option value="unsubscribed">Unsubscribed · funding outreach OK</option>
          <option value="dormant">Dormant (no emails)</option>
        </select>
      </div>

      {/* Summary */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
        <UsersIcon className="h-3.5 w-3.5" />
        Showing <b className="text-neutral-100">{users.length}</b> users
        <span className="text-neutral-700">·</span>
        <span className="text-emerald-300">{counts.founder} founders</span>
        <span className="text-neutral-700">·</span>
        <span className="text-sky-300">{counts.funder} funders</span>
        <span className="text-neutral-700">·</span>
        <span className="text-[#F59E0B]">{counts.admin} admins</span>
      </div>

      {/* Table */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-neutral-900/80 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              <tr>
                <th className={`${STICKY_TH} left-0 w-[128px] min-w-[128px]`}>Signed up</th>
                <th className={`${STICKY_TH} left-[128px] w-[108px] min-w-[108px]`}>Role</th>
                <th className={`${STICKY_TH} left-[236px] w-[240px] min-w-[240px] ${STICKY_EDGE}`}>Email</th>
                <th className="px-4 py-3">Verified</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Business / Mandate</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Revenue band</th>
                <th className="px-4 py-3">Profitable</th>
                <th className="px-4 py-3">Industry</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Approval</th>
                <th className="px-4 py-3">Emails</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {loading ? (
                <tr><td colSpan="14" className="px-4 py-12 text-center text-neutral-500">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="14" className="px-4 py-12 text-center text-neutral-500">No users match these filters.</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="group hover:bg-neutral-900/60" data-testid={`admin-users-row-${u.user_id}`}>
                  <td className={`${STICKY_TD} left-0 w-[128px] min-w-[128px] text-neutral-400`}>{fmtDate(u.created_at)}</td>
                  <td className={`${STICKY_TD} left-[128px] w-[108px] min-w-[108px]`}>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_COLOR[u.role] || "border-neutral-700 text-neutral-300"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className={`${STICKY_TD} left-[236px] w-[240px] min-w-[240px] max-w-[240px] truncate text-neutral-100 ${STICKY_EDGE}`} title={u.email}>{u.email}</td>
                  <td className="px-4 py-3" data-testid={`admin-users-verified-${u.user_id}`}>
                    {u.email_verified === true ? (
                      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300">✓ Verified</span>
                    ) : (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-neutral-300">{renderUserBusinessOrMandate(u)}</td>
                  <td className="px-4 py-3 text-neutral-300">{u.country || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-300" data-testid={`admin-users-band-${u.user_id}`}>
                    {u.role === "founder" ? (QUIZ_BAND_LABELS[u.quiz_revenue_band] || "—") : "—"}
                  </td>
                  <td className="px-4 py-3" data-testid={`admin-users-profit-${u.user_id}`}>
                    {u.role === "founder" && u.quiz_is_profitable ? (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        u.quiz_is_profitable === "yes" ? "bg-emerald-500/10 text-emerald-300"
                          : u.quiz_is_profitable === "no" ? "bg-red-500/10 text-red-300"
                          : "bg-neutral-700/40 text-neutral-300"}`}>
                        {u.quiz_is_profitable === "not_sure" ? "Not sure" : u.quiz_is_profitable}
                      </span>
                    ) : <span className="text-neutral-500">—</span>}
                  </td>
                  <td className="max-w-[160px] truncate px-4 py-3 text-neutral-300" data-testid={`admin-users-industry-${u.user_id}`} title={u.sector || ""}>
                    {u.role === "founder" ? (u.sector || "—") : "—"}
                  </td>
                  <td className="px-4 py-3">{renderUserStatus(u)}</td>
                  <td className="px-4 py-3" data-testid={`admin-users-email-status-${u.user_id}`}>
                    <EmailStatusCell u={u} onChange={(s) => setUserEmailStatus(u, s)} />
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "funder" ? (
                      <ApprovalCell u={u} onApprove={() => approveFunder(u)} onReject={() => rejectFunder(u)} />
                    ) : (
                      <span className="text-neutral-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {u.role === "founder" && u.company_id ? (
                        <>
                          <Link
                            to={`/admin/businesses/${u.company_id}#documents`}
                            data-testid={`admin-users-link-${u.user_id}`}
                            title="Open this business — uploaded documents, readiness score, and AI readiness report."
                            className="inline-flex items-center gap-1 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-2 py-1 text-xs font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/20"
                          >
                            <FolderOpen className="h-3 w-3" /> Open business
                          </Link>
                          <Link
                            to={`/funder/sme/${u.company_id}`}
                            data-testid={`admin-users-funderview-${u.user_id}`}
                            title="View this business through the funder lens — full data room, no approval required."
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20"
                          >
                            <Eye className="h-3 w-3" /> Funder view
                          </Link>
                        </>
                      ) : u.role === "founder" ? (
                        <span className="text-[11px] italic text-neutral-500" data-testid={`admin-users-no-business-${u.user_id}`}>
                          No business yet
                        </span>
                      ) : null}
                      <button
                        onClick={() => setJourneyUser(u)}
                        data-testid={`admin-users-journey-${u.user_id}`}
                        title="Visitor journey — every page they viewed, from anonymous visitor to customer."
                        className="inline-flex items-center gap-1 rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/20"
                      >
                        <Footprints className="h-3 w-3" /> Journey
                      </button>
                      <button
                        onClick={() => deleteUser(u)}
                        data-testid={`admin-users-delete-${u.user_id}`}
                        title="Delete user"
                        className="rounded p-1 text-neutral-500 hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <InviteAdminModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <UserJourneyModal user={journeyUser} onClose={() => setJourneyUser(null)} />
    </AdminShell>
  );
}

const _APPROVAL_BADGE = {
  pending:  { cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",   Icon: Clock },
  rejected: { cls: "border-red-500/40 bg-red-500/10 text-red-300",         Icon: XCircle },
  approved: { cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", Icon: CheckCircle2 },
};

function ApprovalCell({ u, onApprove, onReject }) {
  const status = u.funder_approval_status || "approved"; // legacy = approved
  const isPending = status === "pending";
  const isRejected = status === "rejected";
  const { cls: badgeCls, Icon } = _APPROVAL_BADGE[status] || _APPROVAL_BADGE.approved;
  return (
    <div className="flex flex-wrap items-center gap-1.5"
         data-testid={`admin-users-approval-${u.user_id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${badgeCls}`}>
        <Icon className="h-3 w-3" /> {status}
      </span>
      {(isPending || isRejected) && (
        <>
          <button onClick={onApprove}
            data-testid={`admin-users-approve-${u.user_id}`}
            title="Approve funder"
            className="rounded p-1 text-emerald-300 hover:bg-emerald-500/10">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </button>
          {!isRejected && (
            <button onClick={onReject}
              data-testid={`admin-users-reject-${u.user_id}`}
              title="Reject funder"
              className="rounded p-1 text-red-300 hover:bg-red-500/10">
              <XCircle className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}
